import { NewStockClassDraft, useStockClassList } from '@commenda/api';
import { useState } from 'react';
import { STAGED_STOCK_CLASS_ID } from '../constants';
import { isCapitalOnlyLlc, useEntityOwnershipNouns } from '../ownershipNouns';

// Shared staging for the share-class picker across Add/Edit shareholder
// forms. Tracks the optional staged draft, exposes the resolved display
// name (staged or existing), and the request body honouring the
// stagedClass XOR existingId contract on the BE.
export function useStockClassStaging(
  corporationId: number,
  selectedStockClassId: string | undefined
) {
  const [stagedStockClass, setStagedStockClass] =
    useState<NewStockClassDraft | null>(null);
  const { data: stockClassList } = useStockClassList(corporationId);
  const { classNouns, tracksUnits } = useEntityOwnershipNouns(corporationId);
  const capitalOnlyLlc = isCapitalOnlyLlc(tracksUnits, stockClassList);

  // A staged draft occupies the field with a marker id, so only a real id
  // addresses an existing class.
  const existingStockClassId =
    selectedStockClassId === STAGED_STOCK_CLASS_ID || !selectedStockClassId
      ? undefined
      : selectedStockClassId;
  const existingStockClass = existingStockClassId
    ? stockClassList?.find((sc) => sc.id === existingStockClassId)
    : undefined;

  // A partnership never names its class, and an LLC that has not elected has not named
  // one either — nothing should label a field with a class the member never chose.
  const selectedStockClassName =
    classNouns === null || capitalOnlyLlc
      ? undefined
      : (stagedStockClass?.name ?? existingStockClass?.name);

  // The BE rejects writes that set both `stockClassId` and `newStockClass`.
  const stockClassBody = stagedStockClass
    ? { newStockClass: stagedStockClass }
    : { stockClassId: existingStockClassId };

  // With nothing picked, an unelected LLC still holds its capital in the one implicit
  // class the server will place the member in — so that is the total to divide by.
  const implicitClass =
    capitalOnlyLlc && existingStockClass === undefined
      ? stockClassList?.[0]
      : undefined;

  const authorizedShares =
    stagedStockClass?.authorizedShares ??
    existingStockClass?.authorizedShares ??
    implicitClass?.authorizedShares ??
    null;

  // Null when authorized shares are unresolved (callers block, not write a 0-share holding).
  // Round, not floor, so re-editing a holding doesn't shed shares.
  const ownedSharesFromPercent = (ownershipPercent: number): number | null => {
    // A 0% control-only holder owns nothing, so there's no class to convert
    // through — don't block on an authorized count they don't need.
    if (ownershipPercent === 0) return 0;
    return authorizedShares != null && authorizedShares > 0
      ? Math.round((authorizedShares * ownershipPercent) / 100)
      : null;
  };

  return {
    capitalOnlyLlc,
    stagedStockClass,
    setStagedStockClass,
    existingStockClassId,
    selectedStockClassName,
    stockClassBody,
    ownedSharesFromPercent,
  };
}
